import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode, CSSProperties } from 'react';
import { api, runWithAuditReason } from '../../api/client';
import '../backlog/backlog.css';
import './morReporting.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, ToolbarDivider, FormMetaChips } from '../../components/AppToolbar';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useReasonGate, useConfirm } from '../../components/confirm';
import { useNavActions } from '../../hooks/useNavActions';
import { LoaderSymbol, runLoading } from '../../components/loading';
import { FormTitle } from '../../components/FormTitle';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { ReportProgressOverlay } from './ReportProgressOverlay';
import { StepHint } from '../../components/StepHint/StepHint';
import { fmtLocalDate, fmtLocalShort } from '../../utils/datetime';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';

// ── Types (mirror AlpacaIntelligence.Application.MorReporting) ──────────────────
interface ListItem {
  oid: string; reportID: string | null; templateName: string | null; reportName: string | null;
  categoryName: string | null; status: number; statusLabel: string;
  dateCollectedFrom: string | null; dateCollectedTo: string | null; dateCreated: string | null;
  createdBy: string | null; dateApproved: string | null; dateDelivered: string | null;
}
interface Location {
  oid: string; siteName: string | null; siteId: string | null; description: string | null;
  address: string | null; samplingAddress: string | null; pwsid: string | null;
  siteCode: string | null; siteNumber: string | null; sampleType: string | null;
}
interface Param {
  oid: string; parameterName: string | null; methodName: string | null;
  synonym: string | null; matrix: string | null; test: string | null; components: string | null;
  rpdSigFig: string | null; rpdCutOff: string | null; rpdDecimal: string | null;
  recSigFig: string | null; recCutOff: string | null; recDecimal: string | null;
}
interface Detail {
  oid: string; reportID: string | null; templateId: string | null; templateName: string | null;
  reportName: string | null; categoryId: string | null; categoryName: string | null;
  dateCollectedFrom: string | null; dateCollectedTo: string | null; dateCreated: string | null;
  reportCreatedBy: string | null; dateApproved: string | null; approvedBy: string | null;
  dateDelivered: string | null; deliveredBy: string | null; status: number; statusLabel: string;
  emailTo: string | null; hasPdf: boolean; hasExcel: boolean;
  emailOptions: string[]; sampleLocations: Location[]; parameters: Param[];
}
interface TemplateOpt { id: string; templateName: string | null; reportName: string | null; categoryId: string | null; categoryName: string | null; emailTo: string | null; }
interface ReportOpt { name: string; }
interface CategoryOpt { id: string; name: string; }

const STATUS = { PendingSubmission: 0, PendingApproval: 1, PendingDelivery: 2, Delivered: 3 } as const;
const dateInput = (s?: string | null) => (s ? s.slice(0, 10) : '');

export default function MorReportingPage({ pageId = 'morlist', statusFilter, breadcrumb = 'MOR Reporting' }: {
  /** navData id driving the FormTitle (morlist | morpapp | morpdel). */
  pageId?: string;
  /** When set, the list is restricted to this/these MorReportStatus value(s)
   *  (e.g. the Pending Approval / Pending Delivery work queues) and New/Delete
   *  are hidden. Accepts a single status or an array — MOR Pending Delivery
   *  needs BOTH PendingDelivery and Delivered (ALPACA LIMS parity: legacy's
   *  DrinkingWaterQualityReports_ListView_PendingDelivery criteria is
   *  "[Status] = PendingDelivery Or [Status] = Delivered", not PendingDelivery
   *  alone — a delivered report must stay visible here, not vanish). */
  statusFilter?: number | number[];
  breadcrumb?: string;
} = {}) {
  const notify  = useNotify();
  const runSave = useSaveFlow();
  const gateReason = useReasonGate();
  const { confirm } = useConfirm();
  const isQueue = statusFilter != null;
  // Effective MAC action permissions for this MOR form (New=create, Delete=delete).
  // Hides buttons the user's Module Access Control level doesn't grant; falls open
  // for admins / when no MAC level applies.
  const perm = useNavActions(pageId);

  const [rows, setRows]       = useState<ListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [search, setSearch]   = useState('');
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  // Detail state (null = list view)
  const [detail, setDetail]   = useState<Detail | null>(null);
  const [mode, setMode]       = useState<'view' | 'edit' | 'add'>('view');
  const [templates, setTemplates] = useState<TemplateOpt[]>([]);
  const [reportOpts, setReportOpts] = useState<ReportOpt[]>([]);
  const [categories, setCategories] = useState<CategoryOpt[]>([]);
  const [dCategoryId, setDCategoryId] = useState('');
  const [busy, setBusy]       = useState(false);
  // Drives the shared ReportProgressOverlay (the "Generating…" bar + elapsed
  // clock Custom / Ad Hoc Reporting use) for BOTH Preview AND Excel export —
  // they render through the same engine, so both get the paced progress.  The
  // label is per-action so the overlay paces each independently.
  const [rendering, setRendering] = useState(false);
  const [renderLabel, setRenderLabel] = useState('Generating report PDF...');
  // Stop button on the progress overlay genuinely aborts the in-flight
  // render request (prototype fix #3, 2026-07-03) — never just hides it.
  const renderAbortRef = useRef<AbortController | null>(null);
  const [matched, setMatched] = useState<number | null>(null);

  // ── Date Range filter (default 3M — same as Custom Reporting / Tracking forms) ──
  const [dateRangeDays, setDateRangeDays] = useState<number | null>(90);

  // Draft (editable fields)
  const [dTemplateId, setDTemplateId] = useState('');
  const [dReportName, setDReportName] = useState('');
  const [dFrom, setDFrom] = useState('');
  const [dTo, setDTo]     = useState('');
  const [dEmails, setDEmails] = useState<Set<string>>(new Set());

  // ── List load ───────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      if (dateRangeDays != null) {
        const since = new Date();
        since.setDate(since.getDate() - dateRangeDays);
        params.since = since.toISOString().slice(0, 10);
      }
      const res = await api.get('/api/v1/mor-reporting', { params });
      setRows((res.data as { data: ListItem[] }).data ?? []);
    } catch (e: unknown) {
      setError(apiErr(e, 'Failed to load MOR reports'));
    } finally {
      setLoadMs(performance.now() - t0); setLoading(false);
    }
  }, [dateRangeDays]);
  useEffect(() => { void load(); }, [load]);

  const loadLookups = useCallback(async () => {
    try {
      const [t, r, c] = await Promise.all([
        api.get('/api/v1/mor-reporting/lookups/templates'),
        api.get('/api/v1/mor-reporting/lookups/reports'),
        api.get('/api/v1/mor-reporting/lookups/categories'),
      ]);
      setTemplates((t.data as { data: TemplateOpt[] }).data ?? []);
      setReportOpts((r.data as { data: ReportOpt[] }).data ?? []);
      setCategories((c.data as { data: CategoryOpt[] }).data ?? []);
    } catch { /* dropdowns optional */ }
  }, []);

  const filtered = useMemo(() => {
    const statuses = statusFilter == null ? null
      : Array.isArray(statusFilter) ? new Set(statusFilter) : new Set([statusFilter]);
    const base = statuses ? rows.filter(r => statuses.has(r.status)) : rows;
    if (!search.trim()) return base;
    const t = search.toLowerCase();
    return base.filter(r => [r.reportID, r.templateName, r.reportName, r.categoryName, r.statusLabel]
      .some(v => (v ?? '').toLowerCase().includes(t)));
  }, [rows, search, statusFilter]);

  // Keep the selection in sync with what's actually DISPLAYED.  A report that
  // leaves this queue (Approve/Deliver/Rollback) drops out of `filtered` but may
  // still exist in `rows` under a new status — so reconcile against `filtered`,
  // not all rows.  Otherwise a stale id keeps selected.size > 1 and wrongly
  // disables Edit/Delete.  (Runs on filtered changes only — never on selection.)
  useEffect(() => {
    setSelected(prev => {
      if (prev.size === 0) return prev;
      const ids = new Set<string | number>(filtered.map(r => r.oid));
      const kept = [...prev].filter(id => ids.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [filtered]);

  const cols: AppColumn<ListItem>[] = [
    { field: 'reportID', header: 'Report ID', width: 140,
      render: (row) => {
        const id = row.reportID;
        if (!id) return '—';
        return (
          <a href="#"
            style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
            onClick={async (e) => {
              e.preventDefault();
              if (!row.oid) return;
              try {
                const res = await runLoading(
                  api.get(`/api/v1/mor-reporting/${row.oid}/preview`, { responseType: 'blob' }),
                  { blocking: true, label: 'Generating preview…' });
                const url = URL.createObjectURL(res.data as Blob);
                if (!window.open(url, '_blank'))
                  notify.info('Preview ready — allow pop-ups for this site to open it.');
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
              } catch (err: unknown) {
                notify.warning(await blobErr(err, 'Preview failed.'));
              }
            }}
          >{id}</a>
        );
      },
    },
    { field: 'templateName', header: 'Template',     width: 200 },
    { field: 'reportName',   header: 'Report',       width: 200 },
    { field: 'categoryName', header: 'Category',     width: 140 },
    { field: 'dateCollectedFrom', header: 'Collected From', width: 130, type: 'date' },
    { field: 'dateCollectedTo',   header: 'Collected To',   width: 130, type: 'date' },
    { field: 'dateCreated',  header: 'Date Created',  width: 130, type: 'date' },
    { field: 'createdBy',    header: 'Created By',    width: 200 },
    { field: 'dateDelivered', header: 'Delivery Date', width: 130, type: 'date' },
    { field: 'deliveredBy',   header: 'Delivery By',   width: 150 },
    { field: 'statusLabel',  header: 'Status',       width: 150, type: 'status' },
  ];

  // ── Detail open / close ───────────────────────────────────────────────────────
  async function openDetail(oid: string) {
    setBusy(true);
    await loadLookups();
    try {
      const res = await api.get(`/api/v1/mor-reporting/${oid}`);
      const d = (res.data as { data: Detail }).data;
      applyDetail(d, d.status === STATUS.PendingSubmission ? 'edit' : 'view');
    } catch (e: unknown) {
      notify.error(apiErr(e, 'Could not load report.'));
    } finally { setBusy(false); }
  }
  async function openNew() {
    setBusy(true);
    await loadLookups();
    setDetail({
      oid: '', reportID: null, templateId: null, templateName: null, reportName: null,
      categoryId: null, categoryName: null, dateCollectedFrom: null, dateCollectedTo: null,
      dateCreated: null, reportCreatedBy: null, dateApproved: null, approvedBy: null,
      dateDelivered: null, deliveredBy: null, status: 0, statusLabel: 'Pending Submission',
      emailTo: null, hasPdf: false, hasExcel: false, emailOptions: [], sampleLocations: [], parameters: [],
    });
    setMode('add'); setDTemplateId(''); setDReportName(''); setDCategoryId(''); setDFrom(''); setDTo('');
    setDEmails(new Set()); setMatched(null); setBusy(false);
  }
  /** Toolbar Edit — opens the single ticked report. Same entry point as the row
   *  double-click and the Load button; openDetail decides edit vs read-only. */
  function editSelected() {
    const oid = [...selected][0];
    if (oid) void openDetail(String(oid));
  }
  function applyDetail(d: Detail, m: 'view' | 'edit') {
    setDetail(d); setMode(m);
    setDTemplateId(d.templateId ?? ''); setDReportName(d.reportName ?? ''); setDCategoryId(d.categoryId ?? '');
    setDFrom(dateInput(d.dateCollectedFrom)); setDTo(dateInput(d.dateCollectedTo));
    setDEmails(new Set((d.emailTo ?? '').split(/[,;]/).map(s => s.trim()).filter(Boolean)));
    setMatched(null);
  }
  function closeDetail() { setDetail(null); void load(); }

  // ── Template change → autofill ─────────────────────────────────────────────────
  async function onTemplateChange(templateId: string) {
    setDTemplateId(templateId);
    if (!templateId) return;
    try {
      const res = await api.get(`/api/v1/mor-reporting/templates/${templateId}/autofill`);
      const a = (res.data as { data: Detail }).data;
      setDReportName(a.reportName ?? '');
      setDCategoryId(a.categoryId ?? '');
      setDetail(prev => prev ? {
        ...prev, templateId, templateName: a.templateName, reportName: a.reportName,
        categoryId: a.categoryId, categoryName: a.categoryName,
        sampleLocations: a.sampleLocations ?? [], parameters: a.parameters ?? [],
        emailOptions: a.emailOptions ?? [],
      } : prev);
      setDEmails(new Set(a.emailOptions ?? []));   // default: all recipients ticked
    } catch (e: unknown) {
      notify.error(apiErr(e, 'Autofill failed.'));
    }
  }

  // ── Matched-count preview when template + dates set ────────────────────────────
  useEffect(() => {
    if (!dTemplateId || !dFrom || !dTo) { setMatched(null); return; }
    let alive = true;
    api.get('/api/v1/mor-reporting/matched-count', { params: { templateId: dTemplateId, from: dFrom, to: dTo } })
      .then(r => { if (alive) setMatched((r.data as { data: { count: number } }).data.count); })
      .catch(() => { if (alive) setMatched(null); });
    return () => { alive = false; };
  }, [dTemplateId, dFrom, dTo]);

  const editable = mode === 'add' || mode === 'edit';

  // All categories from dbo.ReportCategory (loaded via lookups/categories).
  const categoryOpts = categories;

  function buildPayload() {
    return {
      templateId: dTemplateId || null,
      reportName: dReportName || null,
      categoryId: dCategoryId || null,
      dateCollectedFrom: dFrom || null,
      dateCollectedTo: dTo || null,
      emailTo: Array.from(dEmails).join(', ') || null,
    };
  }

  /** Validate the required fields (Template + Report are mandatory — legacy
   *  RuleRequiredField).  Returns an error message or null. */
  function validateRequired(): string | null {
    if (!dTemplateId) return 'Template is required.';
    if (!dReportName) return 'Report is required.';
    return null;
  }

  async function handleSave() {
    const err = validateRequired();
    if (err) { notify.warning(err); return; }
    if (busy) return;
    setBusy(true);
    try {
      await runSave({
        // Reason-for-change gate (standing rule 11) — only on an edit (Create exempt).
        formName: 'MOR Reporting',
        auditAction: mode === 'add' ? 'create' : 'edit',
        save: async () => {
          if (mode === 'add') {
            const res = await api.post('/api/v1/mor-reporting', buildPayload());
            const id = (res.data as { data: { id: string } }).data.id;
            const d = await api.get(`/api/v1/mor-reporting/${id}`);
            applyDetail((d.data as { data: Detail }).data, 'edit');
          } else {
            await api.put(`/api/v1/mor-reporting/${detail!.oid}`, buildPayload());
            const d = await api.get(`/api/v1/mor-reporting/${detail!.oid}`);
            applyDetail((d.data as { data: Detail }).data, 'edit');
          }
        },
        successMessage: 'Saved successfully.',
        successAs: 'toast',
        onError: (e) => notify.error(`Save failed: ${apiErr(e, 'Could not save.')}`),
      });
    } finally { setBusy(false); }
  }

  async function handleSaveAndSubmit() {
    const err = validateRequired();
    if (err) { notify.warning(err); return; }
    if (busy) return;
    const ok = await confirm({ title: 'Save & Submit', message: 'Save and submit this report for approval?', confirmLabel: 'Save & Submit' });
    if (!ok) return;
    setBusy(true);
    try {
      // Step 1 — save (create)
      const res = await api.post('/api/v1/mor-reporting', buildPayload());
      const id = (res.data as { data: { id: string } }).data.id;
      // Step 2 — submit
      await api.post(`/api/v1/mor-reporting/${id}/submit`);
      notify.success('Report submitted for approval.');
      closeDetail();
    } catch (e: unknown) {
      notify.error(apiErr(e, 'Save & Submit failed.'));
    } finally { setBusy(false); }
  }

  /** Preview / Export — auto-saves the report first when it's a New unsaved
   *  form (Preview needs a persisted report + matched samples), then opens. */
  async function fileFlow(kind: 'preview' | 'excel') {
    if (!dTemplateId) { notify.warning('Select a Template.'); return; }
    if (!dFrom || !dTo) { notify.warning('Enter Date Collected From and To.'); return; }
    if (busy) return;
    setBusy(true);
    setRenderLabel(kind === 'preview' ? 'Generating report PDF...' : 'Generating report Excel...');
    setRendering(true);
    renderAbortRef.current?.abort();
    const ac = new AbortController();
    renderAbortRef.current = ac;
    try {
      let blob: Blob;
      if (detail?.oid) {
        // Saved report → render from the stored/live data (no new row).
        const res = await api.get(`/api/v1/mor-reporting/${detail.oid}/${kind}`,
          { responseType: 'blob', signal: ac.signal });
        blob = res.data as Blob;
      } else {
        // New / unsaved report → render directly from the current form values.
        // IMPORTANT: this does NOT save the report — a row is created only on Save.
        const path = kind === 'preview' ? 'preview' : 'export-excel';
        const res = await api.post(`/api/v1/mor-reporting/${path}`, {
          templateId: dTemplateId,
          reportName: dReportName || null,
          dateCollectedFrom: dFrom || null,
          dateCollectedTo: dTo || null,
        }, { responseType: 'blob', signal: ac.signal });
        blob = res.data as Blob;
      }
      const url = URL.createObjectURL(blob);
      if (kind === 'preview') { window.open(url, '_blank'); }
      else {
        // ALPACA names the export by Report ID (e.g. DWQR2606034.xlsx); an unsaved
        // report has no ID yet, so fall back to the selected Report layout name.
        const baseName = (detail?.reportID || dReportName || 'MOR').replace(/[^\w.-]+/g, '_');
        const a = document.createElement('a');
        a.href = url; a.download = `${baseName}.xlsx`; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: unknown) {
      if (!ac.signal.aborted) {
        const msg = await blobErr(e, `${kind === 'preview' ? 'Preview' : 'Export'} failed.`);
        // Preview failures (no sample data, layout not found, sidecar needed on dev)
        // → blocking warning modal so the user can read the full reason.
        // Export failures are unexpected → blocking error modal.
        if (kind === 'preview') notify.warning(msg);
        else notify.error(msg);
      }
    } finally { setBusy(false); setRendering(false); }
  }

  async function runAction(path: string, okMsg: string) {
    if (!detail) return;
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/api/v1/mor-reporting/${detail.oid}/${path}`);
      notify.success(okMsg);
      // ALPACA closes the detail (View.Close()) after Approve / Deliver / Roll Back /
      // Submit — return to the list and refresh it so the report moves out of this
      // queue into the next status.
      closeDetail();
      await load();
    } catch (e: unknown) {
      notify.error(apiErr(e, 'Action failed.'));
    } finally { setBusy(false); }
  }

  async function handleDelete() {
    if (!detail) return;
    if (!(await confirm({ title: 'Delete report?', message: `Delete report ${detail.reportID}?`, tone: 'danger', confirmLabel: 'Delete' }))) return;
    const reason = await gateReason('MOR Reporting', 'delete');
    if (reason === null) return;
    setBusy(true);
    try {
      await runWithAuditReason(reason, () => api.delete(`/api/v1/mor-reporting/${detail.oid}`));
      notify.success('Report deleted.');
      closeDetail();
    } catch (e: unknown) {
      notify.error(apiErr(e, 'Delete failed.'));
    } finally { setBusy(false); }
  }

  /** Soft-delete every selected list row (one DELETE per row). */
  async function deleteSelectedList() {
    if (selected.size === 0) { notify.warning('Select at least one report to delete.'); return; }
    const targets = rows.filter(r => selected.has(r.oid));
    if (!(await confirm({
      title: targets.length === 1 ? 'Delete report?' : 'Delete reports?',
      message: `Delete ${targets.length} report${targets.length === 1 ? '' : 's'}? (${targets.map(t => t.reportID).join(', ')})`,
      tone: 'danger', confirmLabel: 'Delete',
    }))) return;
    const reason = await gateReason('MOR Reporting', 'delete');
    if (reason === null) return;
    setLoading(true);
    try {
      await runWithAuditReason(reason, () => Promise.all(targets.map(t => api.delete(`/api/v1/mor-reporting/${t.oid}`))));
      setSelected(new Set());
      await load();
      notify.success(`Deleted ${targets.length} report${targets.length === 1 ? '' : 's'}.`);
    } catch (e: unknown) {
      notify.error(apiErr(e, 'Delete failed.'));
      await load();
    } finally {
      setLoading(false);
    }
  }


  // ── Render ─────────────────────────────────────────────────────────────────────
  const locCols: AppColumn<Location>[] = [
    { field: 'siteName', header: 'Site Name', width: 180 },
    { field: 'description', header: 'Description', width: 200 },
    { field: 'address', header: 'Address', width: 180 },
    { field: 'samplingAddress', header: 'Sampling Address', width: 180 },
    { field: 'pwsid', header: 'PWS ID', width: 120 },
    { field: 'siteCode', header: 'Site Code', width: 110 },
    { field: 'siteNumber', header: 'Site Number', width: 120 },
    { field: 'sampleType', header: 'Sample Type', width: 130 },
  ];
  const parCols: AppColumn<Param>[] = [
    { field: 'parameterName', header: 'Parameter', width: 200 },
    { field: 'synonym', header: 'Synonym', width: 130 },
    { field: 'matrix', header: 'Matrix', width: 110 },
    { field: 'test', header: 'Test', width: 150 },
    { field: 'methodName', header: 'Method', width: 240 },
    { field: 'components', header: 'Components', width: 130 },
    { field: 'rpdSigFig', header: 'RPDSigFig', width: 100 },
    { field: 'rpdCutOff', header: 'RPDCutOff', width: 100 },
    { field: 'rpdDecimal', header: 'RPDDecimal', width: 100 },
    { field: 'recSigFig', header: 'RECSigFig', width: 100 },
    { field: 'recCutOff', header: 'RECCutOff', width: 100 },
    { field: 'recDecimal', header: 'RECDecimal', width: 100 },
  ];
  const st = detail?.status ?? 0;

  // ── Workflow stepper (shared TopbarStepper, BO UI Standard §5) ──────────────
  const stepperSteps: IslandStep[] | null = (() => {
    const anySel = selected.size > 0;
    const sel = (): IslandStep['state'] => (anySel || !!detail ? 'done' : 'active');
    const inEdit = mode === 'edit' || mode === 'add';
    if (pageId === 'morpapp')
      // Matches prototype: Select a Report ID → Load for Approve → Save (If Edit) → Approve
      return [
        { id: 'select', label: 'Select a Report ID', state: sel() },
        { id: 'load',   label: 'Load for Approve',    state: !anySel && !detail ? 'todo' : detail ? 'done' : 'active' },
        { id: 'save',   label: 'Save (If Edit)',      state: !detail ? 'todo' : inEdit ? 'active' : 'done' },
        { id: 'approve',label: 'Approve',             state: !detail ? 'todo' : st >= STATUS.PendingDelivery ? 'done' : 'active' },
      ];
    if (pageId === 'morpdel')
      // Matches prototype: Select a Report ID → Load for Deliver → Save (If Edit) → Deliver
      return [
        { id: 'select', label: 'Select a Report ID', state: sel() },
        { id: 'load',   label: 'Load for Deliver',    state: !anySel && !detail ? 'todo' : detail ? 'done' : 'active' },
        { id: 'save',   label: 'Save (If Edit)',      state: !detail ? 'todo' : inEdit ? 'active' : 'done' },
        { id: 'deliver',label: 'Deliver',             state: !detail ? 'todo' : st >= STATUS.Delivered ? 'done' : 'active' },
      ];
    // morlist — MOR Reporting: stepper only shows INSIDE the form (detail view).
    // On the list view the stepper is hidden so the topbar stays clean.
    if (!detail) return null;
    const isNew = mode === 'add';
    return [
      { id: 'query',   label: 'Query Mandatory', state: isNew ? 'active' : 'done' },
      { id: 'preview', label: 'Preview',          state: isNew ? 'todo' : (detail.oid ? 'done' : 'active') },
      { id: 'save',    label: 'Save',              state: !detail.oid ? 'todo' : 'done' },
      { id: 'submit',  label: 'Submit',            state: st >= STATUS.PendingApproval ? 'done' : (!detail.oid ? 'todo' : 'active') },
    ];
  })();

  // ── Active-step guidance (StepHint companion to the Dynamic Island) ────────
  // Narrates only the ACTIVE step — what to do right now.  Updates live as
  // the stepper advances.  Never lists all steps (the Island does that).
  const listHint = (() => {
    const hasSel = selected.size > 0;
    if (pageId === 'morpapp') {
      if (!hasSel && !detail)
        return <><b>Step 1 · Select Reports.</b> Tick one or more reports from the list, then click <b>Load</b> to open and review.</>;
      return <><b>Step 1 · Done.</b> Click <b>Load</b> to open the selected report for review.</>;
    }
    if (pageId === 'morpdel') {
      if (!hasSel && !detail)
        return <><b>Step 1 · Select Reports.</b> Tick one or more reports from the list, then click <b>Load</b> to open and verify the delivery details.</>;
      return <><b>Step 1 · Done.</b> Click <b>Load</b> to open the selected report and confirm delivery details.</>;
    }
    // morlist — MOR Reporting
    if (!hasSel && !detail)
      return <><b>Step 1 · Select Report.</b> Click a row to open it, or click <b>New</b> to create a report.</>;
    return <><b>Step 1 · Done.</b> Click <b>Load</b> to open the selected report, or <b>New</b> to create one.</>;
  })();

  const detailHint = (() => {
    if (!detail) return null;
    if (pageId === 'morpapp')
      return <><b>Step 2 · Approve.</b> Review the report details, then click <b>Approve</b> to move it to Pending Delivery.</>;
    if (pageId === 'morpdel')
      return <><b>Step 2 · Review &amp; Deliver.</b> Verify the recipient email and collected-date range, then click <b>Deliver</b> to send the report.</>;
    // morlist
    if (mode === 'add')
      return <><b>Step 1 · Query Mandatory fields.</b> <b>Template</b>, <b>Report</b> and <b>Date Collected</b> are all required.</>;
    if (st === STATUS.PendingSubmission)
      return <><b>Step 2 · Preview.</b> Choose a <b>Template</b> and date range, click <b>Preview</b> to generate the PDF, then <b>Save</b> and <b>Submit</b> when ready.</>;
    if (st === STATUS.PendingApproval)
      return <><b>Step 3 · Submitted.</b> Report is awaiting approval. You can still <b>Roll Back</b> to revise it.</>;
    return <><b>Complete.</b> This report has been delivered. Use <b>Back</b> to return to the list.</>;
  })();

  // Phase 2.3 — MOR Reporting keyboard shortcuts (CLAUDE.md standing rule 14,
  // §18.6, ADR-0013).  DETAIL view = the status-gated action set; LIST view =
  // Alt+L to load the selected report (New/Edit/Preview/Refresh/Export come from
  // the list AppToolbar, which auto-registers them).  Each entry mirrors the
  // button's render/disabled condition so a key never fires for a hidden action.
  useHotkeys(detail ? [
    // Back — return to the list (Esc, never fires mid-typing).
    { combo: 'esc', label: 'Back', group: 'MOR Reporting', order: 1, allowInInput: false,
      run: () => closeDetail() },
    // Save — visible in add/edit for a user who can create.
    { combo: 'mod+s', label: 'Save', group: 'MOR Reporting', order: 2,
      disabled: busy || !((mode === 'add' || mode === 'edit') && perm.create),
      run: () => { void handleSave(); } },
    // Submit — Save & Submit (new report) OR the status action (Pending Submission).
    { combo: 'alt+u', label: 'Submit', group: 'MOR Reporting', order: 3,
      disabled: busy || !((mode === 'add' && perm.create) || (!!detail?.oid && st === STATUS.PendingSubmission && perm.write)),
      run: () => {
        if (mode === 'add' && perm.create) void handleSaveAndSubmit();
        else if (detail?.oid && st === STATUS.PendingSubmission && perm.write) void runAction('submit', 'Report submitted.');
      } },
    // Approve — MOR Pending Approval queue.
    { combo: 'alt+a', label: 'Approve', group: 'MOR Reporting', order: 4,
      disabled: busy || !(detail?.oid && st === STATUS.PendingApproval && perm.write),
      run: () => { void runAction('approve', 'Report approved.'); } },
    // Deliver — MOR Pending Delivery queue.
    { combo: 'alt+y', label: 'Deliver', group: 'MOR Reporting', order: 5,
      disabled: busy || !(detail?.oid && st === STATUS.PendingDelivery && perm.write),
      run: () => { void runAction('deliver', 'Report delivered.'); } },
    // Preview — a saved report or a chosen template can be previewed.
    { combo: 'alt+p', label: 'Preview', group: 'MOR Reporting', order: 6,
      disabled: busy || !(detail?.oid || dTemplateId),
      run: () => { void fileFlow('preview'); } },
    // Export to Excel — same availability, not on the read-only queues.
    { combo: 'alt+x', label: 'Export to Excel', group: 'MOR Reporting', order: 7,
      disabled: busy || isQueue || !(detail?.oid || dTemplateId),
      run: () => { void fileFlow('excel'); } },
    // Delete — only a Pending Submission draft.
    { combo: 'alt+d', label: 'Delete', group: 'MOR Reporting', order: 8,
      disabled: busy || !(detail?.oid && st === STATUS.PendingSubmission),
      run: () => { void handleDelete(); } },
  ] : [
    { combo: 'alt+l', label: 'Load', group: 'MOR Reporting', order: 1,
      run: () => { const oid = [...selected][0]; if (oid) void openDetail(String(oid)); },
      disabled: selected.size !== 1 },
  ]);

  return (
    <>
      <style>{MR_CSS}</style>
      {/* Dynamic Island stepper — always mounted (list AND detail) so the pill
          stays centred in the topbar throughout the full workflow. */}
      <TopbarStepper steps={stepperSteps} />
      <div className="bl-page">
        {/* LIST VIEW — shown when no detail is open.  New / row-click opens the
            full-page DETAIL below (BO UI Standard §4 detail-view concept — the
            detail REPLACES the list inline, not a popup; user request 2026-07-09). */}
        {!detail && (
        <>
            <div className="bl-top">
              <div className="dp-page-hdr">
                <div className="dp-breadcrumb">
                  <span>Home</span><i className="ti ti-chevron-right" />
                  <span>Reporting</span><i className="ti ti-chevron-right" />
                  <span className="dp-bc-active">{breadcrumb}</span>
                </div>
                {/* Shared FormTitle + Load action (top-right, prototype parity). */}
                <div className="dp-title-row">
                  <FormTitle pageId={pageId} loadMs={loadMs} loading={loading} auditFormName="MOR Reporting" />
                  {/* Load — primary workflow advance; enabled when exactly one report is selected.
                      Mirrors Custom Reporting's Load button position (top-right of title row). */}
                  <button
                    className="ca-btn ca-btn-solid"
                    onClick={() => { const oid = [...selected][0]; if (oid) void openDetail(String(oid)); }}
                    disabled={selected.size !== 1}
                    title={(selected.size !== 1
                      ? 'Tick exactly one report to load.'
                      : 'Load the selected report for review.') + ` (${formatCombo('alt+l')})`}>
                    <span aria-hidden="true">⬇️</span>&nbsp;Load
                  </button>
                </div>
                <div className="dp-subtitle mlv-subcap">
                  {pageId === 'morpapp'
                    ? `Read-only · ${filtered.length} items · MOR reports awaiting approval — filtered view of MOR Reporting, read-only monitoring (approve/E-Sign happens on Report eSignature).`
                    : pageId === 'morpdel'
                    ? `Read-only · ${filtered.length} items · MOR reports past approval — filtered view of MOR Reporting, read-only monitoring.`
                    : `${filtered.length} items · Every Monthly Operating Report (drinking-water quality report) on file.`}
                </div>
              </div>
            </div>
            {/* Grid area: toolbar + search sit ON the grey page (BO UI Standard v2);
                only the grid card below is white. */}
            <div className="bl-grid-area">
              {/* Active-step guidance strip — same position as Custom Reporting (above toolbar, on grey page). */}
              <StepHint tone="guidance">{listHint}</StepHint>
              {/* Toolbar on its OWN full-width row — canonical order. */}
              <div style={{ margin: '0 0 8px' }}>
                {(() => {
                  const selRows = filtered.filter(r => selected.has(r.oid));
                  const anyDelivered = selRows.some(r => r.status === STATUS.Delivered);
                  return (
                <AppToolbar
                  newLabel="New"
                  onNew={isQueue || !perm.create ? undefined : openNew}
                  // Edit — ALPACA parity (its MOR Reporting list has the stock XAF
                  // "Edit" action + per-row pencil, both of which just open that row's
                  // detail). Canonical slot between New and Delete (§18.6). Opens the
                  // ticked report; openDetail then decides edit vs read-only from status,
                  // so a Delivered report still opens — read-only, as it does in ALPACA.
                  onEdit={editSelected}
                  editDisabled={selected.size !== 1 || busy}
                  onDelete={perm.delete ? deleteSelectedList : undefined}
                  deleteDisabled={selected.size === 0 || anyDelivered}
                  onPreview={pageId === 'morpdel' ? async () => {
                    const selRows = filtered.filter(r => selected.has(r.oid));
                    if (selRows.length !== 1) return;
                    try {
                      const res = await runLoading(
                        api.get(`/api/v1/mor-reporting/${selRows[0]!.oid}/preview`, { responseType: 'blob' }),
                        { blocking: true, label: 'Generating preview…' });
                      const url = URL.createObjectURL(res.data as Blob);
                      if (!window.open(url, '_blank'))
                        notify.info('Preview ready — allow pop-ups for this site to open it.');
                      setTimeout(() => URL.revokeObjectURL(url), 60_000);
                    } catch (e: unknown) {
                      notify.warning(await blobErr(e, 'Preview failed.'));
                    }
                  } : undefined}
                  previewDisabled={selected.size !== 1 || busy}
                  onRefresh={load}
                  refreshing={loading}
                  exportData={{ filename: 'mor-reports', ...gridExportMatrix(filtered, cols) }}
                  left={
                    !isQueue
                      ? <DateRangeSelect
                          variant="dropdown"
                          label="Date Range"
                          ranges={TRACKING_RANGES}
                          value={dateRangeDays}
                          onChange={setDateRangeDays}
                        />
                      : undefined
                  }
                />
                  );
                })()}
              </div>
              {/* Search — shared .list-search-row on the grey page, never inside the card. */}
              <div className="list-search-row">
                <div className="list-search">
                  <span aria-hidden="true">🔍</span>
                  <input type="text" placeholder="Search reports…" value={search} onChange={e => setSearch(e.target.value)} />
                  {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
                </div>
              </div>
              {/* ONE white grid card — grid + its single footer only. */}
              <div className="bl-card bl-card-flex">
                {error ? (
                  <div className="bl-err" style={{ margin: 12 }}><i className="ti ti-alert-circle" /> {error}
                    <button onClick={load} style={retryStyle}>Retry</button></div>
                ) : (
                  <AppDataGrid<ListItem> rows={filtered} columns={cols} getRowId={r => r.oid}
                    loading={loading} loadMs={loadMs}
                    selectable selectedIds={selected} onSelectionChange={setSelected}
                    // Row Edit — the shared grid's standard affordance (standing rule 12),
                    // matching ALPACA's per-row pencil: open that report directly.
                    onRowDoubleClick={r => { void openDetail(r.oid); }}
                    emptyText="No MOR reports found" />
                )}
              </div>
            </div>
          </>
        )}
        {/* DETAIL VIEW — full-page, REPLACES the list inline (BO UI Standard §4
            detail-view concept, not a popup; user request 2026-07-09). */}
        {detail && (
          <>
            <div className="bl-top">
              <div className="dp-page-hdr">
                {/* Breadcrumb — Settings-detail style: Home › Reporting › {form} › {record}. */}
                <div className="dp-breadcrumb">
                  <span>Home</span><i className="ti ti-chevron-right" />
                  <span>Reporting</span><i className="ti ti-chevron-right" />
                  <span className="mr-link" onClick={closeDetail}>{breadcrumb}</span>
                  <i className="ti ti-chevron-right" />
                  <span className="dp-bc-active">{detail.reportID ?? 'New Report'}{detail.templateName ? ` — ${detail.templateName}` : ''}</span>
                </div>
              </div>
              <div className="section-hdr" style={hdrStyle}>
                {/* Title in the SettingsDetailPage form: "{BO} – {record}". */}
                <FormTitle pageId={pageId} title={`${breadcrumb} – ${detail.reportID ?? 'New Report'}`}
                  auditFormName="MOR Reporting" auditSourceId={detail.oid || undefined} />
                <AppToolbar
                  showMeta={false}
                  left={<>
                    <button className="ca-btn" onClick={closeDetail} title={`Back (${formatCombo('esc')})`}><i className="ti ti-arrow-left" /> Back</button>
                    <ToolbarDivider />
                    {(mode === 'add' || mode === 'edit') && perm.create && (
                      <button className="ca-btn" onClick={handleSave} disabled={busy} title={`Save (${formatCombo('mod+s')})`}><span aria-hidden="true">💾</span> Save</button>
                    )}
                    {mode === 'add' && perm.create && (
                      <button className="ca-btn ca-btn-solid" onClick={handleSaveAndSubmit} disabled={busy} title={`Submit (${formatCombo('alt+u')})`}><span aria-hidden="true">📤</span> Submit</button>
                    )}
                    {/* The stage-advancing action is the bar's ONE solid button
                        (Submit / Approve / Deliver — only one shows at a time). */}
                    {detail.oid && st === STATUS.PendingSubmission && perm.write && (
                      <button className="ca-btn ca-btn-solid" onClick={() => runAction('submit', 'Report submitted.')} disabled={busy} title={`Submit (${formatCombo('alt+u')})`}><span aria-hidden="true">📤</span> Submit</button>
                    )}
                    {/* Approve / Deliver — shown on the MOR Pending Approval / Pending
                        Delivery work queues (legacy btn_DWQRApprove / btn_DWQRDeliver).
                        Approve: Pending Approval → Pending Delivery (stamps DateApproved/
                        ApprovedBy).  Deliver: Pending Delivery → Delivered. */}
                    {detail.oid && st === STATUS.PendingApproval && perm.write && (
                      <button className="ca-btn ca-btn-solid" onClick={() => runAction('approve', 'Report approved.')} disabled={busy} title={`Approve (${formatCombo('alt+a')})`}><span aria-hidden="true">✓</span> Approve</button>
                    )}
                    {detail.oid && st === STATUS.PendingDelivery && perm.write && (
                      <button className="ca-btn ca-btn-solid" onClick={() => runAction('deliver', 'Report delivered.')} disabled={busy} title={`Deliver (${formatCombo('alt+y')})`}><span aria-hidden="true">📤</span> Deliver</button>
                    )}
                  </>}
                />
              </div>
            </div>

            <div className="bl-grid-area" style={{ overflow: 'auto' }}>
              {/* Active-step guidance for the detail view. */}
              {detailHint && (
                <StepHint tone="guidance" className="mr-detail-hint">
                  {detailHint}
                </StepHint>
              )}
              {/* Secondary action row — Preview / Export to Excel / Delete — sits
                  below the step hint so the top bar stays clean (Back · Save · stage action). */}
              {((detail.oid || dTemplateId) || (detail.oid && st === STATUS.PendingSubmission)) && (
                <div style={{ display: 'flex', gap: 6, margin: '0 0 8px' }}>
                  {(detail.oid || dTemplateId) && (
                    <button className="ca-btn" onClick={() => fileFlow('preview')} disabled={busy} title={`Preview (${formatCombo('alt+p')})`}><span aria-hidden="true">👁</span> Preview</button>
                  )}
                  {!isQueue && (detail.oid || dTemplateId) && (
                    <button className="ca-btn" onClick={() => fileFlow('excel')} disabled={busy} title={`Export to Excel (${formatCombo('alt+x')})`}><span aria-hidden="true">📊</span> Export to Excel</button>
                  )}
                  {detail.oid && st === STATUS.PendingSubmission && (
                    <button className="ca-btn" onClick={handleDelete} disabled={busy} title={`Delete (${formatCombo('alt+d')})`}><span aria-hidden="true">🗑</span> Delete</button>
                  )}
                  {/* Loaded + Audit chips sit at the END of this PRIMARY action row
                      (after Preview / Export to Excel / Delete), per the global
                      rule — the header bar above passes showMeta={false}. */}
                  <FormMetaChips />
                </div>
              )}
              {/* Preview PDF generation → full "Generating report PDF…" overlay
                  with the elapsed clock (same as Custom / Ad Hoc Reporting).
                  Other busy work keeps the lightweight corner toast. */}
              {rendering && <ReportProgressOverlay label={renderLabel}
                                                   onStop={() => renderAbortRef.current?.abort()} />}
              {busy && !rendering && <div className="mr-busy"><LoaderSymbol size={14} /> Working…</div>}

              {/* General Information — 2-column layout matching ALPACA Golive:
                  left = operational inputs; right = audit/status read-only fields. */}
              <div className="bl-card" style={{ marginBottom: 12, flexShrink: 0 }}>
                <div className="mr-sec">General Information</div>
                <div className="mr-geninfo">
                  {/* LEFT column — Report ID · Template · Report · Category · Date Collected */}
                  <div className="mr-col">
                    <F label="Report ID"><div className="mr-val">{detail.reportID ?? 'Autogenerate'}</div></F>
                    <F label="Template *">
                      {editable ? (
                        <select className={!dTemplateId ? 'mr-req-empty' : ''} value={dTemplateId} onChange={e => onTemplateChange(e.target.value)} disabled={mode === 'edit' && st !== STATUS.PendingSubmission}>
                          <option value="">— Select —</option>
                          {templates.map(t => <option key={t.id} value={t.id}>{t.templateName}</option>)}
                        </select>
                      ) : <div className="mr-val">{detail.templateName || '—'}</div>}
                    </F>
                    <F label="Report *">
                      {editable ? (
                        <select className={!dReportName ? 'mr-req-empty' : ''} value={dReportName} onChange={e => setDReportName(e.target.value)}>
                          <option value="">— Select —</option>
                          {dReportName && !reportOpts.some(r => r.name === dReportName) && <option value={dReportName}>{dReportName}</option>}
                          {reportOpts.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
                        </select>
                      ) : <div className="mr-val">{detail.reportName || '—'}</div>}
                    </F>
                    <F label="Category">
                      {editable ? (
                        <select value={dCategoryId} onChange={e => {
                          const opt = categoryOpts.find(c => c.id === e.target.value);
                          setDCategoryId(e.target.value);
                          setDetail(prev => prev ? { ...prev, categoryId: e.target.value || null, categoryName: opt?.name ?? null } : prev);
                        }}>
                          <option value="">— Select —</option>
                          {categoryOpts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      ) : <div className="mr-val">{detail.categoryName || 'N/A'}</div>}
                    </F>
                    <F label="Date Collected *">
                      {editable ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
                          <input type="date" className={!dFrom ? 'mr-req-empty' : ''} value={dFrom} onChange={e => setDFrom(e.target.value)} style={{ flex: 1 }} />
                          <span style={{ color: 'var(--ai-muted)', fontSize: 12 }}>→</span>
                          <input type="date" className={!dTo ? 'mr-req-empty' : ''} value={dTo} onChange={e => setDTo(e.target.value)} style={{ flex: 1 }} />
                        </div>
                      ) : (
                        <div className="mr-val">
                          {detail.dateCollectedFrom ? fmtLocalDate(detail.dateCollectedFrom) : '—'}
                          {' — '}
                          {detail.dateCollectedTo ? fmtLocalDate(detail.dateCollectedTo) : '—'}
                        </div>
                      )}
                    </F>
                    {matched !== null && (
                      <F label="Matching Samples"><div className="mr-val"><span className={matched > 0 ? 'mr-pill-ok' : 'mr-pill-warn'}>{matched} sample-parameter{matched === 1 ? '' : 's'}</span></div></F>
                    )}
                    <F label="Status"><div className="mr-val"><span className="mr-status">{detail.statusLabel}</span></div></F>
                  </div>
                  {/* RIGHT column — audit read-only fields */}
                  <div className="mr-col">
                    <F label="Date Created"><div className="mr-val">{detail.dateCreated ? fmtLocalShort(detail.dateCreated) : 'N/A'}</div></F>
                    <F label="Created By"><div className="mr-val">{detail.reportCreatedBy || 'N/A'}</div></F>
                    <F label="Date Approved"><div className="mr-val">{detail.dateApproved ? fmtLocalShort(detail.dateApproved) : 'N/A'}</div></F>
                    <F label="Approved By"><div className="mr-val">{detail.approvedBy || 'N/A'}</div></F>
                    <F label="Date Delivered"><div className="mr-val">{detail.dateDelivered ? fmtLocalShort(detail.dateDelivered) : 'N/A'}</div></F>
                    <F label="Delivered By"><div className="mr-val">{detail.deliveredBy || 'N/A'}</div></F>
                  </div>
                </div>
              </div>

              {/* Sample Locations */}
              <div className="bl-card" style={{ marginBottom: 12, flexShrink: 0 }}>
                <div className="mr-sec">Sample Locations <span className="mr-count">{detail.sampleLocations.length}</span></div>
                <AppDataGrid<Location> rows={detail.sampleLocations} columns={locCols} getRowId={r => r.oid}
                  defaultPageSize={20} emptyText="No sample locations (pick a template)." />
              </div>

              {/* Parameters */}
              <div className="bl-card" style={{ flexShrink: 0 }}>
                <div className="mr-sec">Parameters <span className="mr-count">{detail.parameters.length}</span></div>
                <AppDataGrid<Param> rows={detail.parameters} columns={parCols} getRowId={r => r.oid}
                  defaultPageSize={20} emptyText="No parameters (pick a template)." />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function F({ label, full, children }: { label: string; full?: boolean; children: ReactNode }) {
  return <div className={`mr-field${full ? ' mr-field-full' : ''}`}><label>{label}</label>{children}</div>;
}
function apiErr(e: unknown, fallback: string): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return r?.data?.error?.message ?? (e instanceof Error ? e.message : fallback);
}

/** Parse an error from a blob-typed response (Preview/Export use responseType:'blob',
 *  so a 4xx body arrives as a Blob of JSON rather than a parsed object).
 *  Always returns a string — never throws — so callers can safely pass it to notify.info/error. */
async function blobErr(e: unknown, fallback: string): Promise<string> {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { error?: { message?: string } };
      if (parsed?.error?.message) return parsed.error.message;
    } catch { /* not JSON — fall through */ }
  }
  return apiErr(e, fallback);
}

const hdrStyle: CSSProperties = { padding: '8px 14px', borderBottom: '1px solid var(--ai-line)', margin: 0, background: 'var(--ai-header-bg)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const retryStyle: CSSProperties = { marginLeft: 10, color: 'var(--brand-accent, #e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' };

const MR_CSS = `
  .mr-link { color:var(--ai-blue); cursor:pointer; } .mr-link:hover { text-decoration:underline; }
  /* Right-sized detail POPUP (standing rule 12): hugs the form, scrolls inside. */
  .mr-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center;
    justify-content:center; z-index:9000; backdrop-filter:blur(2px); padding:16px; }
  .mr-detail-modal { width:min(1180px, 97vw); max-height:93vh; background:#fff; border-radius:10px;
    overflow:hidden; display:flex; flex-direction:column; box-shadow:0 18px 50px rgba(0,0,0,.3); }
  .mr-detail-modal .bl-top { flex:0 0 auto; }
  .mr-detail-modal .bl-grid-area { flex:1 1 auto; min-height:0; }
  .mr-busy { position:fixed; top:80px; right:24px; z-index:50; background:#1e3c5a; /* token-exempt: white-on-dark toast */ color:#fff; padding:6px 12px; border-radius:6px; font-size:12px; display:flex; gap:6px; align-items:center; }
  .mr-sec { font-size:13px; font-weight:700; color:var(--ai-ink); padding:10px 14px; border-bottom:1px solid var(--ai-line); background:var(--ai-header-bg); }
  .mr-count { display:inline-block; min-width:18px; text-align:center; background:var(--ai-muted); color:#fff; border-radius:9px; font-size:10px; padding:1px 7px; margin-left:6px; }
  .mr-grid { display:grid; grid-template-columns:1fr 1fr 1fr 1fr; gap:12px 20px; padding:16px; }
  @media (max-width: 1200px){ .mr-grid { grid-template-columns:1fr 1fr 1fr; } }
  @media (max-width: 900px){ .mr-grid { grid-template-columns:1fr 1fr; } }
  @media (max-width: 600px){ .mr-grid { grid-template-columns:1fr; } }
  /* ALPACA "General Information" — two ordered columns */
  .mr-geninfo { display:grid; grid-template-columns:1fr 1fr; gap:6px 40px; padding:12px 16px 8px; }
  @media (max-width: 820px){ .mr-geninfo { grid-template-columns:1fr; } }
  .mr-col { display:flex; flex-direction:column; gap:6px; }
  .mr-daterow { display:grid; grid-template-columns:1fr 1fr; gap:12px 16px; }
  .mr-fullrow { padding:4px 16px 16px; }
  /* Inline label:control layout — label on left, control on right (same row). */
  .mr-field { display:flex; flex-direction:row; align-items:center; gap:8px; min-height:30px; } .mr-field-full { grid-column:1 / -1; }
  .mr-field label { font-size:12px; color:var(--ai-muted); font-weight:600; white-space:nowrap; min-width:120px; max-width:120px; text-align:right; flex-shrink:0; }
  .mr-field > select, .mr-field > input { flex:1; min-width:0; }
  .mr-field input, .mr-field select { font-size:13px; padding:5px 8px; border:1px solid var(--ai-line); border-radius:4px; background:#fff; color:var(--ai-ink); outline:none; }
  .mr-field input:focus, .mr-field select:focus { border-color:var(--brand-accent, #e8670a); }
  .mr-field input:disabled, .mr-field select:disabled { background:var(--ai-readonly-bg); color:var(--ai-muted); }
  /* Required-but-empty fields: yellow tint (standing rule 12 / §14b spec). */
  .mr-field input.mr-req-empty, .mr-field select.mr-req-empty { background:#fffbe7; }
  .mr-val { font-size:13px; padding:4px 0; color:var(--ai-ink); flex:1; min-width:0; }
  .mr-status { background:var(--ai-pill-info-bg); color:var(--ai-pill-info-fg); border-radius:10px; padding:2px 10px; font-size:12px; font-weight:600; }
  .mr-pill-ok { background:var(--ai-pill-ok-bg); color:var(--ai-pill-ok-fg); border-radius:10px; padding:2px 10px; font-size:12px; font-weight:600; }
  .mr-pill-warn { background:var(--ai-pill-alert-bg); color:var(--ai-pill-alert-fg); border-radius:10px; padding:2px 10px; font-size:12px; font-weight:600; }
  .mr-emails { display:flex; flex-wrap:wrap; gap:6px 16px; padding:4px 0; }
  .mr-email { display:inline-flex; align-items:center; gap:6px; font-size:12px; color:var(--ai-ink); }
  .mr-detail-hint { margin:12px 0 8px; }
`;
